import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useRoute, useLocation } from "wouter";
import {
  ArrowLeft, Calendar, DollarSign, Edit, Loader2, FileText,
  AlertCircle, Package,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { DateCell } from "@/components/DateCell";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/cn";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";

interface SalesAgreementLine {
  id: string;
  lineNumber: number;
  description: string | null;
  committedQuantity: string | null;
  consumedQuantity: string | null;
  unitPrice: string | null;
  uom: string;
  status: string;
  product?: { id: string; name: string; sku: string | null };
}

interface SalesAgreement {
  id: string;
  agreementNumber: string;
  status: string;
  accountId: string | null;
  commitmentType: string;
  fulfillmentType: string;
  committedValue: string | null;
  consumedValue: string | null;
  paymentTerms: string | null;
  validFrom: string;
  validTo: string;
  approvedAt: string | null;
  notes: string | null;
  internalNotes: string | null;
  createdAt: string;
  account?: { id: string; name: string };
  lines?: SalesAgreementLine[];
}

const STATUSES = [
  { value: "DRAFT", label: "Draft" },
  { value: "ACTIVE", label: "Active" },
  { value: "SUSPENDED", label: "Suspended" },
  { value: "EXPIRED", label: "Expired" },
  { value: "CLOSED", label: "Closed" },
  { value: "CANCELLED", label: "Cancelled" },
];

function formatStatusLabel(s: string) {
  return s.replace(/_/g, " ").split(" ").map(w => w[0] + w.slice(1).toLowerCase()).join(" ");
}

export default function SalesAgreementDetailPage() {
  const { sessionId } = useAuth();
  const [, params] = useRoute("/sales-agreements/:id");
  const [, setLocation] = useLocation();
  const [editOpen, setEditOpen] = useState(false);
  const [editStatus, setEditStatus] = useState("DRAFT");
  const [editNotes, setEditNotes] = useState("");
  const [editInternalNotes, setEditInternalNotes] = useState("");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const id = params?.id;

  const { data: sa, isLoading, error } = useQuery<SalesAgreement>({
    queryKey: ["/api/sales-agreements", id],
    queryFn: () => api.get<SalesAgreement>(`/sales-agreements/${id}`, sessionId),
    enabled: !!id,
  });

  const updateMutation = useMutation({
    mutationFn: (payload: Record<string, any>) =>
      api.patch<SalesAgreement>(`/sales-agreements/${id}`, payload, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/sales-agreements", id] });
      queryClient.invalidateQueries({ queryKey: ["/api/sales-agreements"] });
      toast({ title: "Sales agreement updated" });
      setEditOpen(false);
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const handleEditOpen = () => {
    if (sa) {
      setEditStatus(sa.status || "DRAFT");
      setEditNotes(sa.notes || "");
      setEditInternalNotes(sa.internalNotes || "");
    }
    setEditOpen(true);
  };

  const handleSave = () => {
    updateMutation.mutate({
      status: editStatus,
      notes: editNotes || null,
      internalNotes: editInternalNotes || null,
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-12 w-full rounded-lg" />
        <Skeleton className="h-[300px] w-full rounded-lg" />
      </div>
    );
  }

  if (error || !sa) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <AlertCircle className="h-12 w-12 text-muted-foreground" />
        <p className="text-muted-foreground">Sales agreement not found</p>
        <Button variant="outline" onClick={() => setLocation("/sales-agreements")}>
          Back to Sales Agreements
        </Button>
      </div>
    );
  }

  const lines = sa.lines || [];
  const committedValue = Number(sa.committedValue || 0);
  const consumedValue = Number(sa.consumedValue || 0);
  const consumedPercent = committedValue > 0 ? Math.round((consumedValue / committedValue) * 100) : 0;

  const statusDotColor = (() => {
    switch (sa.status) {
      case "DRAFT": return "bg-gray-400";
      case "ACTIVE": return "bg-green-500";
      case "SUSPENDED": return "bg-orange-500";
      case "EXPIRED": return "bg-red-400";
      case "CLOSED": return "bg-slate-500";
      case "CANCELLED": return "bg-red-500";
      default: return "bg-gray-400";
    }
  })();

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setLocation("/sales-agreements")}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <h1 className="text-2xl font-bold tracking-tight" data-testid="text-agreement-number">
                {sa.agreementNumber}
              </h1>
            </div>
            <div className="flex items-center gap-2 text-2xl font-bold tracking-tight pl-7 flex-wrap">
              {sa.account && sa.accountId && (
                <button
                  onClick={() => setLocation(`/accounts/${sa.accountId}`)}
                  className="text-blue-600 dark:text-blue-400 hover:underline"
                  data-testid="link-account"
                >
                  {sa.account.name}
                </button>
              )}
              {sa.account && <span className="text-muted-foreground font-normal">/</span>}
              <span className="text-muted-foreground font-normal">Status:</span>
              <span data-testid="text-agreement-status">{formatStatusLabel(sa.status)}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={handleEditOpen} data-testid="button-edit-agreement">
              <Edit className="h-3.5 w-3.5 mr-1.5" /> Edit
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-0 border rounded-lg bg-card overflow-x-auto">
          <div className="flex items-center gap-2 px-4 py-2.5 whitespace-nowrap">
            <span className="text-xs text-muted-foreground">Status</span>
            <div className="flex items-center gap-1.5">
              <span className={cn("h-2 w-2 rounded-full shrink-0", statusDotColor)} />
              <span className="text-sm font-medium">{sa.status.replace(/_/g, " ")}</span>
            </div>
          </div>
          <div className="w-px h-8 bg-border" />
          <div className="flex items-center gap-2 px-4 py-2.5 whitespace-nowrap">
            <DollarSign className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <div>
              <span className="text-xs text-muted-foreground">Committed</span>
              <p className="text-sm font-medium">
                {committedValue ? `$${committedValue.toLocaleString()}` : "—"}
              </p>
            </div>
          </div>
          <div className="w-px h-8 bg-border" />
          <div className="flex items-center gap-2 px-4 py-2.5 whitespace-nowrap">
            <DollarSign className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <div>
              <span className="text-xs text-muted-foreground">Consumed</span>
              <p className="text-sm font-medium">
                ${consumedValue.toLocaleString()} ({consumedPercent}%)
              </p>
            </div>
          </div>
          <div className="w-px h-8 bg-border" />
          <div className="flex items-center gap-2 px-4 py-2.5 whitespace-nowrap">
            <Calendar className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <div>
              <span className="text-xs text-muted-foreground">Valid From</span>
              <p className="text-sm font-medium">
                <DateCell value={sa.validFrom} fallback="—" />
              </p>
            </div>
          </div>
          <div className="w-px h-8 bg-border" />
          <div className="flex items-center gap-2 px-4 py-2.5 whitespace-nowrap">
            <Calendar className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <div>
              <span className="text-xs text-muted-foreground">Valid To</span>
              <p className="text-sm font-medium">
                <DateCell value={sa.validTo} fallback="—" />
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        <div className="border-l-[3px] border-blue-500 bg-card rounded-r-lg shadow-sm">
          <div className="px-4 py-3">
            <h2 className="text-sm font-semibold flex items-center gap-2">
              <FileText className="h-4 w-4 text-blue-500" />
              Agreement Details
            </h2>
          </div>
          <div className="px-4 pb-4 space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Commitment Type</span>
              <span className="text-sm font-medium uppercase">{sa.commitmentType}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Fulfillment Type</span>
              <span className="text-sm font-medium">{sa.fulfillmentType?.replace(/_/g, " ")}</span>
            </div>
            {sa.paymentTerms && (
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Payment Terms</span>
                <span className="text-sm font-medium">{sa.paymentTerms}</span>
              </div>
            )}
            {sa.approvedAt && (
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Approved</span>
                <span className="text-sm font-medium"><DateCell value={sa.approvedAt} /></span>
              </div>
            )}
          </div>
        </div>

        <div className="border-l-[3px] border-gray-400 bg-card rounded-r-lg shadow-sm">
          <div className="px-4 py-3">
            <h2 className="text-sm font-semibold flex items-center gap-2">
              <FileText className="h-4 w-4 text-gray-400" />
              Notes
            </h2>
          </div>
          <div className="px-4 pb-4 space-y-3">
            {sa.notes ? (
              <div>
                <span className="text-xs text-muted-foreground">External Notes</span>
                <p className="text-sm whitespace-pre-wrap">{sa.notes}</p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No notes added.</p>
            )}
            {sa.internalNotes && (
              <div>
                <span className="text-xs text-muted-foreground">Internal Notes</span>
                <p className="text-sm whitespace-pre-wrap">{sa.internalNotes}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="border-l-[3px] border-emerald-500 bg-card rounded-r-lg shadow-sm">
        <div className="flex items-center justify-between gap-2 px-4 py-3">
          <h2 className="text-sm font-semibold flex items-center gap-2">
            <Package className="h-4 w-4 text-emerald-500" />
            Line Items
          </h2>
        </div>
        <div className="px-4 pb-4">
          {lines.length > 0 ? (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]">#</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead>SKU</TableHead>
                    <TableHead className="text-right">Committed Qty</TableHead>
                    <TableHead className="text-right">Consumed Qty</TableHead>
                    <TableHead className="text-right">Unit Price</TableHead>
                    <TableHead>UOM</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lines.map((line) => (
                    <TableRow key={line.id}>
                      <TableCell className="font-medium">{line.lineNumber}</TableCell>
                      <TableCell className="font-medium">{line.product?.name || line.description || "—"}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{line.product?.sku || "—"}</TableCell>
                      <TableCell className="text-right font-medium">{Number(line.committedQuantity || 0).toLocaleString()}</TableCell>
                      <TableCell className="text-right">{Number(line.consumedQuantity || 0).toLocaleString()}</TableCell>
                      <TableCell className="text-right font-medium">${Number(line.unitPrice || 0).toFixed(4)}</TableCell>
                      <TableCell>{line.uom}</TableCell>
                      <TableCell>
                        <Badge variant={line.status === "ACTIVE" ? "default" : "secondary"} className="text-xs">
                          {line.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-24 text-muted-foreground">
              <Package className="h-10 w-10 mb-2 opacity-50" />
              <p>No line items</p>
            </div>
          )}
        </div>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Sales Agreement</DialogTitle>
            <DialogDescription>Update the agreement information below.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Status *</label>
              <Select value={editStatus} onValueChange={setEditStatus}>
                <SelectTrigger><SelectValue placeholder="Select status" /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Notes</label>
              <Textarea
                placeholder="External notes..."
                className="min-h-[80px]"
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Internal Notes</label>
              <Textarea
                placeholder="Internal notes..."
                className="min-h-[80px]"
                value={editInternalNotes}
                onChange={(e) => setEditInternalNotes(e.target.value)}
              />
            </div>
            <div className="flex flex-wrap justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={updateMutation.isPending}>
                {updateMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
